// Implements: F-UIX-020, F-UIX-024
/**
 * The `design` module's client for the profile routes of W1f and the image and
 * pin routes of W2g, on top of the single HTTP exit.
 *
 * Every write is sent with `retry: false` (checklist 13): a save or an upload
 * is not idempotent, and a retry after a lost response would store a second
 * version the person never asked for; a re-pin, an activation and a clearing
 * (W2l) are the person's decision, made once.
 */

import { API_BASE } from "../../../shared/api/apiBase";
import { httpRequest } from "../../../shared/api/httpClient";
import type {
  MockupDesignView,
  ProfileDetailView,
  ProfileListingView,
  ProfileVersionValues,
  ProfileSummaryView,
  RenameProfileInput,
  RepinInput,
  ScopeKeyView,
  UploadAssetInput,
  UploadedAssetView,
} from "../../../types/design";

export const PROFILES_PATH = `${API_BASE}/design/profiles`;

export function profilePath(profileId: string): string {
  return `${PROFILES_PATH}/${encodeURIComponent(profileId)}`;
}

export async function fetchProfileListing(): Promise<ProfileListingView | undefined> {
  return httpRequest<ProfileListingView>(PROFILES_PATH);
}

export async function fetchProfile(profileId: string): Promise<ProfileDetailView | undefined> {
  return httpRequest<ProfileDetailView>(profilePath(profileId));
}

export interface CreateProfileInput {
  /** A scope the listing says the caller may edit (W2l); the server decides again. */
  readonly scope: ScopeKeyView;
  readonly name: string;
  readonly values: ProfileVersionValues;
}

export async function createProfile(
  input: CreateProfileInput,
): Promise<ProfileDetailView | undefined> {
  return httpRequest<ProfileDetailView>(PROFILES_PATH, {
    method: "POST",
    body: {
      scope_kind: input.scope.scope_kind,
      scope_id: input.scope.scope_id,
      // A personal scope has no organization, and the route reads an absent one as none.
      ...(input.scope.organization_id === null ? {} : { organization_id: input.scope.organization_id }),
      name: input.name,
      ...input.values,
    },
    retry: false,
  });
}

/** Where a scope's active mark lives: the scope key in the path, its organization as a query value. */
export function activeProfilePath(scope: ScopeKeyView): string {
  const path = `${API_BASE}/design/scopes/${encodeURIComponent(scope.scope_kind)}/${encodeURIComponent(scope.scope_id)}/active-profile`;
  return scope.organization_id === null
    ? path
    : `${path}?organization_id=${encodeURIComponent(scope.organization_id)}`;
}

/** Mark a profile of the scope active; it styles only mockups created afterwards (P6-OD-20). */
export async function activateProfile(
  scope: ScopeKeyView,
  profileId: string,
): Promise<ProfileSummaryView | undefined> {
  return httpRequest<ProfileSummaryView>(activeProfilePath(scope), {
    method: "PUT",
    body: { profile_id: profileId },
    retry: false,
  });
}

/** Remove the scope's mark: new mockups fall to the next level of the cascade (P6-OD-17). */
export async function clearActiveProfile(scope: ScopeKeyView): Promise<void> {
  await httpRequest<undefined>(activeProfilePath(scope), { method: "DELETE", retry: false });
}

export async function addProfileVersion(
  profileId: string,
  values: ProfileVersionValues,
): Promise<ProfileDetailView | undefined> {
  return httpRequest<ProfileDetailView>(`${profilePath(profileId)}/versions`, {
    method: "POST",
    body: values,
    retry: false,
  });
}

/**
 * Where one image of one version is read. The browser asks for it with the
 * session cookie (same origin) and the server only ever answers one of the three
 * raster types, `nosniff`, so an SVG is never rendered from here (W2g).
 */
export function assetPath(profileId: string, version: number, assetId: string): string {
  return `${profilePath(profileId)}/versions/${String(version)}/assets/${encodeURIComponent(assetId)}`;
}

/** Attach one image to a profile; the server saves it as the next version. */
export async function uploadAsset(
  profileId: string,
  input: UploadAssetInput,
): Promise<UploadedAssetView | undefined> {
  return httpRequest<UploadedAssetView>(`${profilePath(profileId)}/assets`, {
    method: "POST",
    body: input,
    retry: false,
  });
}

export function mockupDesignPath(mockupId: string): string {
  return `${API_BASE}/mockups/${encodeURIComponent(mockupId)}/design`;
}

export async function fetchMockupDesign(mockupId: string): Promise<MockupDesignView | undefined> {
  return httpRequest<MockupDesignView>(mockupDesignPath(mockupId));
}

/** Move a mockup's pin; only ever sent when the person asks for it (F-UIX-024). */
export async function repinMockup(
  mockupId: string,
  input: RepinInput,
): Promise<MockupDesignView | undefined> {
  return httpRequest<MockupDesignView>(mockupDesignPath(mockupId), {
    method: "PUT",
    body: input,
    retry: false,
  });
}

/** Rename a profile in place (F-DSG-001): no new version; the answer is its summary. */
export async function renameProfile(
  profileId: string,
  input: RenameProfileInput,
): Promise<ProfileSummaryView | undefined> {
  return httpRequest<ProfileSummaryView>(profilePath(profileId), {
    method: "PATCH",
    body: { name: input.name },
    retry: false,
  });
}

/**
 * Delete a profile (owner 2026-10-19): the server archives it, so it leaves the listing and the
 * cascade while the mockups pinned to it keep their versions. Sent once (`retry: false`).
 */
export async function deleteProfile(profileId: string): Promise<void> {
  await httpRequest<unknown>(profilePath(profileId), { method: "DELETE", retry: false });
}

/**
 * "Duplicar y editar" (owner 2026-10-19, answer 4A): the server copies the profile, with its logo
 * and reference images, into the same scope and answers the copy. Not idempotent: a retry would
 * store a second copy, so it is sent once (`retry: false`).
 */
export async function duplicateProfile(profileId: string): Promise<ProfileDetailView | undefined> {
  return httpRequest<ProfileDetailView>(`${profilePath(profileId)}/duplicate`, {
    method: "POST",
    retry: false,
  });
}
